import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoUrl from '@/assets/LogoForni.png';
import selloUrl from '@/assets/SelloGarantiaForni.png';
import type { MantenimientoRow, DespachoDetalleSummary } from '../api';

/**
 * Certificado de Garantía PDF — branded post-puesta-en-marcha document
 * Fornitalia issues to the customer. Layout (top → bottom):
 *
 *   ┌────────────────────────────────────────────────────────┐
 *   │ Certificado de Garantía          [FN][FORNITALIA logo] │
 *   │ dd/mm/yyyy                                             │
 *   │                                                        │
 *   │ Cliente            │ Dirección                         │
 *   │  <name>            │  <address>                        │
 *   │                                                        │
 *   │ Cel        │ No.Doc │ Referencia │ Nro FC              │
 *   │  <phone>   │ <cuit> │  <nrCot>   │  <factura>          │
 *   │ ─────────────────────────────────────────────────────  │
 *   │ ┃ IMPORTANTE:                                          │
 *   │ ┃ 3-line warranty disclosure                           │
 *   │                                                        │
 *   │ ┌──────────────────┬─────────────────────────────────┐ │
 *   │ │ Modelo           │ Nro de Serie                    │ │
 *   │ ├──────────────────┼─────────────────────────────────┤ │
 *   │ │ rows…                                              │ │
 *   │ └──────────────────┴─────────────────────────────────┘ │
 *   │                                                        │
 *   │ ┌──────────────────────┐                       [SELLO] │
 *   │ │ Cert. y aprobado por │                               │
 *   │ │ <responsable>        │                               │
 *   │ └──────────────────────┘                               │
 *   └────────────────────────────────────────────────────────┘
 *
 * Replaces the prior red-band header design with a cleaner white
 * layout that matches the new brand-team mockup, including the round
 * "CERTIFICADO DE GARANTÍA APROBADO" sello on the bottom-right.
 */

export type CertificadoGarantiaInput = {
  venta: MantenimientoRow;
  detalles: DespachoDetalleSummary[];
  direccionDespacho: string;
  recibe: string;
  /** Firma responsable (admin name). Defaults to the auth usuario_app. */
  responsable: string;
  /** Cliente phone — empty string if unknown. */
  telefono?: string;
  /** Cliente CUIT/DNI — empty string if unknown. */
  noDoc?: string;
  /** External invoice number (legal FC) — empty string if not yet known. */
  nroFc?: string;
};

const BRAND_RED: [number, number, number] = [206, 44, 55];
const TEXT_MUTED: [number, number, number] = [110, 110, 110];
const BORDER_COLOR: [number, number, number] = [220, 220, 220];

export async function generateCertificadoGarantiaPDF(
  input: CertificadoGarantiaInput,
): Promise<Blob> {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;

  // --- top: title (left) + logo (right) ---
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('Certificado de Garantía', margin, margin + 4);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...TEXT_MUTED);
  doc.text(formatArgDate(new Date()), margin, margin + 11);
  doc.setTextColor(0, 0, 0);

  const logoDataUrl = await loadImageAsDataUrl(logoUrl);
  if (logoDataUrl) {
    // Aspect ratio for LogoForni ≈ 3.6:1; render at 38mm wide.
    const logoW = 38;
    const props = doc.getImageProperties(logoDataUrl);
    const logoH = (props.height / props.width) * logoW;
    doc.addImage(
      logoDataUrl,
      'PNG',
      pageWidth - margin - logoW,
      margin - 1,
      logoW,
      logoH,
    );
  }

  let y = margin + 22;

  // --- info grid: Cliente / Dirección row ---
  const f = input.venta.fields;
  const cliente = f.Cliente_RO?.trim() || '—';
  const direccion = input.direccionDespacho?.trim() || '—';
  const cel = (input.telefono ?? '').trim() || '—';
  const noDoc = (input.noDoc ?? '').trim() || '—';
  const referencia = f.NrCotizacion_RO?.toString().trim() || input.venta.id;
  // ResumenOperaciones doesn't carry an FC/factura number column — the
  // legal invoice is generated externally (Contabilium). Leave blank
  // when not provided by the caller; admins can fill it in by hand.
  const nroFc = (input.nroFc ?? '').trim() || '—';

  drawInfoRow(doc, y, [
    { label: 'Cliente', value: cliente, width: 70 },
    { label: 'Dirección', value: direccion, width: pageWidth - margin * 2 - 70 },
  ], margin);
  y += 16;

  drawInfoRow(doc, y, [
    { label: 'Cel', value: cel, width: 40 },
    { label: 'No.Doc', value: noDoc, width: 40 },
    { label: 'Referencia', value: referencia, width: 40 },
    { label: 'Nro FC', value: nroFc, width: pageWidth - margin * 2 - 120 },
  ], margin);
  y += 14;

  // --- separator ---
  doc.setDrawColor(...BORDER_COLOR);
  doc.setLineWidth(0.4);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  // --- IMPORTANTE block with red left bar ---
  const importanteText = [
    'El presente documento certifica la instalación y correcto funcionamiento del producto validado por el técnico.',
    'Sin este certificado no se hará efectiva la garantía. Por favor, no lo pierda.',
    'Las garantías van sujetas a lo establecido en el documento "Garantías FN Fornitalia", entregado en la documentación post venta, junto con el producto.',
  ];
  y = drawImportanteBlock(doc, y, margin, pageWidth - margin * 2, importanteText);
  y += 10;

  // --- modelo / nro serie table ---
  const body = input.detalles.map((d) => [
    d.modelo || d.articulo || '—',
    d.currentSerie || '—',
  ]);
  if (body.length === 0) {
    body.push(['—', '—']);
  }
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 10,
      font: 'helvetica',
      cellPadding: { top: 3.5, right: 4, bottom: 3.5, left: 4 },
      lineColor: BORDER_COLOR,
      lineWidth: 0.3,
      textColor: 20,
    },
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: 80,
      fontStyle: 'bold',
      lineWidth: 0,
    },
    head: [['Modelo', 'Nro de Serie']],
    body,
    theme: 'plain',
    columnStyles: {
      0: { cellWidth: 'auto', fontStyle: 'bold' },
      1: { cellWidth: (pageWidth - margin * 2) / 2 },
    },
    didDrawCell: (data) => {
      // Hand-draw a single divider line between rows; theme 'plain'
      // omits all borders so we can do this selectively.
      if (data.section !== 'body') return;
      if (data.row.index === 0) return;
      const { x, y: cellY, width } = data.cell;
      if (data.column.index !== 0) return;
      doc.setDrawColor(...BORDER_COLOR);
      doc.setLineWidth(0.2);
      doc.line(x, cellY, x + width * 2, cellY);
    },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;

  // --- signature card (bottom-left) + sello (bottom-right) ---
  // Anchor to bottom of page so the layout looks consistent regardless
  // of how many article rows the table grew.
  const cardW = 110;
  const cardH = 28;
  const cardX = margin;
  const cardY = pageHeight - margin - cardH;

  doc.setDrawColor(...BORDER_COLOR);
  doc.setLineWidth(0.4);
  doc.roundedRect(cardX, cardY, cardW, cardH, 3, 3);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...TEXT_MUTED);
  doc.text('Certificado y aprobado por', cardX + 5, cardY + 7);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text(input.responsable || '—', cardX + 5, cardY + 14);

  // Sello — round CERTIFICADO DE GARANTÍA APROBADO stamp.
  const selloDataUrl = await loadImageAsDataUrl(selloUrl);
  if (selloDataUrl) {
    const selloSize = 38;
    doc.addImage(
      selloDataUrl,
      'PNG',
      pageWidth - margin - selloSize,
      pageHeight - margin - selloSize,
      selloSize,
      selloSize,
    );
  }

  return doc.output('blob');
}

export function certificadoGarantiaFileName(ventaId: string): string {
  return `CertificadoGarantia-${ventaId}.pdf`;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

type InfoCell = { label: string; value: string; width: number };

function drawInfoRow(
  doc: jsPDF,
  y: number,
  cells: InfoCell[],
  startX: number,
) {
  let x = startX;
  for (const cell of cells) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...TEXT_MUTED);
    doc.text(cell.label, x, y);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    // Truncate value to fit the cell width — long addresses would
    // otherwise overflow into the next column.
    const lines = doc.splitTextToSize(cell.value, cell.width - 4) as string[];
    doc.text(lines[0] ?? '', x, y + 7);
    if (lines.length > 1) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(lines[1], x, y + 12);
    }

    x += cell.width;
  }
  doc.setTextColor(0, 0, 0);
}

function drawImportanteBlock(
  doc: jsPDF,
  y: number,
  startX: number,
  blockWidth: number,
  paragraphs: string[],
): number {
  const padX = 6;
  const padTop = 5;
  const padBottom = 5;
  const lineHeight = 4.2;
  const gapBetween = 1.5;

  // Pre-compute the text height so we can size the bar + bg properly.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  let totalLines = 0;
  const wrapped: string[][] = paragraphs.map((p) => {
    const ls = doc.splitTextToSize(p, blockWidth - padX * 2 - 4) as string[];
    totalLines += ls.length;
    return ls;
  });
  const textBlockHeight =
    totalLines * lineHeight + (paragraphs.length - 1) * gapBetween;
  // Title line + spacing.
  const titleHeight = 5;
  const blockHeight = padTop + titleHeight + 2 + textBlockHeight + padBottom;

  // Red left bar.
  doc.setFillColor(...BRAND_RED);
  doc.rect(startX, y, 1.6, blockHeight, 'F');

  let cursor = y + padTop + 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text('IMPORTANTE:', startX + padX, cursor);
  cursor += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  for (let i = 0; i < wrapped.length; i++) {
    const lines = wrapped[i];
    for (const line of lines) {
      // Render the embedded "Garantías FN Fornitalia" phrase in bold
      // when it appears (paragraph 3) — small touch that matches the
      // mockup. Cheap detection: scan for the literal substring.
      if (line.includes('Garantías FN Fornitalia')) {
        renderLineWithBold(doc, line, startX + padX, cursor, 'Garantías FN Fornitalia');
      } else {
        doc.text(line, startX + padX, cursor);
      }
      cursor += lineHeight;
    }
    if (i < wrapped.length - 1) cursor += gapBetween;
  }
  doc.setTextColor(0, 0, 0);
  return y + blockHeight;
}

/**
 * Renders a single line that contains a substring we want bold.
 * Splits the line into "before / bold / after" segments and lays
 * them out left-to-right by measuring widths.
 */
function renderLineWithBold(
  doc: jsPDF,
  line: string,
  x: number,
  y: number,
  boldSubstr: string,
) {
  const idx = line.indexOf(boldSubstr);
  if (idx < 0) {
    doc.text(line, x, y);
    return;
  }
  const before = line.slice(0, idx);
  const bold = boldSubstr;
  const after = line.slice(idx + boldSubstr.length);

  doc.setFont('helvetica', 'normal');
  doc.text(before, x, y);
  const beforeW = doc.getTextWidth(before);

  doc.setFont('helvetica', 'bold');
  doc.text(bold, x + beforeW, y);
  const boldW = doc.getTextWidth(bold);

  doc.setFont('helvetica', 'normal');
  doc.text(after, x + beforeW + boldW, y);
}

function formatArgDate(d: Date): string {
  const pad2 = (n: number) => String(n).padStart(2, '0');
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

// Cache keyed by URL so logo + sello don't trample each other (the
// prior single-slot cache returned the first-fetched image for every
// subsequent call).
const imageCache = new Map<string, string>();
async function loadImageAsDataUrl(url: string): Promise<string | null> {
  const cached = imageCache.get(url);
  if (cached) return cached;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    imageCache.set(url, dataUrl);
    return dataUrl;
  } catch {
    return null;
  }
}
